import React from 'react';
import cn from 'classnames';
import type { ITimeZone } from '@dealroadshow/uikit-date-time';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import Label from '@creditflowresearch/uilib/Badge/Label';
import IconFilePdf from '@creditflowresearch/uilib/Icon/IconFilePdf';
import { OTHER_SECTOR } from '@/creditflowresearch/domain/Sector';
import { formatDateOrTime } from '@/creditflowresearch/ui/components/ActiveCalendar/helpers/dates';
import { MONTH_DAY_YEAR_FORMAT } from '@dealroadshow/uikit-date-time/dateFormats';
import ResearchGATrack from '@/creditflowresearch/application/GoogleAnalytics/Research';

import styles from '@/creditflowresearch/ui/components/research/common/ResearchTable/researchTable.module.scss';
import linkStyles from '@creditflowresearch/uilib/styles/links.module.scss';
import alignStyles from '@creditflowresearch/uilib/styles/helpers/align.module.scss';

const getTableColumns = (onDocumentClick, timeZone: ITimeZone) => [
  {
    name: 'pseudoSectorCollection',
    title: 'Sector',
    className: styles.colSector,
    width: 50,
    cellCallback: (cellProps) => {
      const pseudoSector = cellProps.cellData[0];
      const sectorOTHR = cellProps.cellData.find(
        (pseudoSector) => pseudoSector.abbreviation === OTHER_SECTOR.abbreviation,
      );
      const sectorColor = sectorOTHR ? sectorOTHR.color : pseudoSector.color;
      const sectorAbbr = sectorOTHR ? sectorOTHR.abbreviation : pseudoSector.abbreviation;
      const tooltipContent = !pseudoSector.pseudoSubsectorCollection.length
        ? pseudoSector.name
        : `${ pseudoSector.name }: ${ pseudoSector.pseudoSubsectorCollection[0].name }`;

      return (
        <Tooltip content={ tooltipContent }>
          <Label
            className={ styles.sectorLabel }
            backgroundColor={ sectorColor }
          >
            { sectorAbbr }
          </Label>
        </Tooltip>
      );
    },
  },
  {
    name: 'title',
    title: 'Name',
    className: styles.colName,
    sortable: true,
    cellCallback: (cellProps) => {
      const documentId = cellProps.row.id;
      const subtitle = cellProps.row.subtitle ? `: ${ cellProps.row.subtitle }` : '';
      const cellString = `${ cellProps.cellData }${ subtitle }`;

      return (
        <span
          className={ linkStyles.link }
          data-test="researchTableTitleCell"
          onClick={ async () => {
            const { documentUrl } = await onDocumentClick(documentId);

            ResearchGATrack.documentClick({
              link: documentUrl,
              research_name: cellProps.cellData,
              sector: cellProps.row.pseudoSectorCollection[0].name,
              subsector: cellProps.row.pseudoSectorCollection?.[0].pseudoSubsectorCollection?.[0]?.name || '',
              publisher: cellProps.row.publisher.displayName,
              publish_date: cellProps.row.publishedAt.dateTime,
              type: cellProps.row.documentTypeCollection.map((type) => type.displayName).join(', '),
            });
          } }
        >
          { cellString }
        </span>
      );
    },
  },
  {
    name: 'documentTypeCollection',
    title: 'Type',
    className: cn(styles.colType, alignStyles.alignRight),
    cellCallback: (cellProps) => cellProps.cellData.map((type) => type.displayName).join(', '),
  },
  {
    name: 'publisher',
    title: 'Publisher',
    className: cn(styles.colPublisher, alignStyles.alignRight),
    width: 100,
    cellCallback: (cellProps) => {
      let publisherName = '';
      let tooltip = '';

      if (cellProps.cellData.displayName === 'DBRS') {
        publisherName = `${ cellProps.cellData.displayName }-MS`;
        tooltip = `${ cellProps.cellData.displayName } - Morningstar`;
      } else {
        return cellProps.cellData.displayName;
      }

      return (
        <Tooltip content={ tooltip }>
          { publisherName }
        </Tooltip>
      );
    },
  },
  {
    name: 'publishedAt',
    title: 'Date',
    sortable: true,
    className: cn(styles.colDate, alignStyles.alignRight),
    width: 120,
    cellCallback: ({ row, cellData }) => {
      const documentId = row.id;
      const { dateTime } = cellData;
      const publishedAt = formatDateOrTime(dateTime, timeZone, MONTH_DAY_YEAR_FORMAT, true);

      return (
        <>
          <div className={ styles.publishedAt }>
            { publishedAt }
          </div>
          <div className={ styles.publishedAtActions }>
            <span
              data-test="researchTableDateCell"
              onClick={ async () => {
                const { documentUrl } = await onDocumentClick(documentId);

                ResearchGATrack.documentClick({
                  link: documentUrl,
                  research_name: row.title,
                  sector: row.pseudoSectorCollection[0].name,
                  subsector: row.pseudoSectorCollection?.[0].pseudoSubsectorCollection?.[0]?.name || '',
                  publisher: row.publisher.displayName,
                  publish_date: row.publishedAt.dateTime,
                  type: row.documentTypeCollection.map((type) => type.displayName).join(', '),
                });
              } }
            >
              <IconFilePdf
                className={ styles.downloadDocumentIcon }
              />
            </span>
          </div>
        </>
      );
    },
  },
];

export default getTableColumns;
